import { useState } from 'react';

import { useIsomorphicLayoutEffect } from './use-isomorphic-layout-effect';

/**
 * This code is copied and modified from the following source:
 * Author: https://usehooks-ts.com/
 * Source: https://github.com/juliencrn/usehooks-ts/blob/master/packages/usehooks-ts/src/useMediaQuery/useMediaQuery.ts
 * License: MIT https://github.com/juliencrn/usehooks-ts/blob/master/LICENSE
 */

/**
 * Hook that tracks whether a CSS media query matches the current viewport.
 * Automatically updates when the viewport changes and handles server-side rendering.
 *
 * @param {string} query - The CSS media query string to evaluate (e.g., '(min-width: 768px)').
 * @param {object} [options] - Configuration options.
 * @param {boolean} [options.defaultValue=false] - The default value to use during SSR or when window is unavailable.
 * @param {boolean} [options.initializeWithValue=true] - Whether to initialize with the current match value or use defaultValue.
 * @returns {boolean} True if the media query matches the current viewport, false otherwise.
 */
export function useMediaQuery(
  query: string,
  { defaultValue = false, initializeWithValue = true } = {},
): boolean {
  const getMatches = (query: string): boolean => {
    if (typeof window === 'undefined') {
      return defaultValue;
    }
    return window.matchMedia(query).matches;
  };

  const [matches, setMatches] = useState<boolean>(() => {
    if (initializeWithValue) {
      return getMatches(query);
    }
    return defaultValue;
  });

  // Handles the change event of the media query.
  function handleChange() {
    setMatches(getMatches(query));
  }

  useIsomorphicLayoutEffect(() => {
    const matchMedia = window.matchMedia(query);

    // Triggered at the first client-side load and if query changes
    handleChange();

    matchMedia.addEventListener('change', handleChange);

    return () => {
      matchMedia.removeEventListener('change', handleChange);
    };
  }, [query]);

  return matches;
}
